import { FeatureType } from 'common/types'

/**
 * How the live session in front of the user is being run.
 *
 * `ONLINE` - a video conference; everybody joins from wherever they are.
 * `IN_PERSON` - everybody shares one physical room and the trainer drives a
 * projector. The stationary interaction scheme enables no conference call, no
 * camera and no microphone at all.
 */
export type SessionMode = 'ONLINE' | 'IN_PERSON'

/**
 * Features a remote interaction scheme enables and a stationary one never
 * does. `ConferenceCallFeatureType` is enabled per room; `MicrophoneType` and
 * `CameraType` are enabled on the **scheme** itself - `TrainingIsFactory`
 * passes `isContext` for those two, not a room context - which is what makes
 * the mode knowable before any room is entered.
 */
const ONLINE_ONLY: FeatureType[] = [
	'ConferenceCallFeatureType',
	'MicrophoneType',
	'CameraType',
]

/** Enabled on the stationary training room, and nowhere in a remote scheme. */
const IN_PERSON_ONLY: FeatureType[] = ['SimpleGrouping']

const includesAny = (features: FeatureType[], any: FeatureType[]): boolean =>
	features.some(feature => any.includes(feature))

/**
 * What one room's feature list says about the mode - `undefined` when it says
 * nothing.
 *
 * Absence of the conference call is *not* by itself evidence of in-person: a
 * lobby has no conference call in either scheme, and for the first frames
 * after joining any room the list is still empty. Only positive evidence
 * counts here, which is why this asks two questions rather than one.
 *
 * Deliberately derived from the live scheme rather than from the training
 * type: a blended training switches type per chunk while keeping one
 * `liveSessionId`.
 */
export const sessionModeOfRoom = (
	roomFeatures: FeatureType[]
): SessionMode | undefined => {
	if (includesAny(roomFeatures, ONLINE_ONLY)) {
		return 'ONLINE'
	}

	if (includesAny(roomFeatures, IN_PERSON_ONLY)) {
		return 'IN_PERSON'
	}

	return undefined
}

/**
 * What the scheme's own feature list says. Unlike a room's, this is always an
 * answer.
 *
 * The scheme-level list is the only signal that is present *wherever* the user
 * is, and it is the reason this function exists: a stationary scheme is
 * constructed in its `BREAK` phase, so a user joining before the trainer
 * starts the session lands in the **lobby**, which in that scheme has an empty
 * feature list - no conference call, no `SimpleGrouping`, nothing to read.
 * Verified against a live stack: a stationary scheme sends
 * `["ScriptFeatureType"]` for the scheme and `[]` for its lobby.
 *
 * A remote scheme enables `MicrophoneType` and `CameraType` on the scheme, so
 * their absence here is positive evidence rather than an unanswered question.
 */
export const sessionModeOfInteractionScheme = (
	schemeFeatures: FeatureType[]
): SessionMode | undefined => {
	if (includesAny(schemeFeatures, ONLINE_ONLY)) {
		return 'ONLINE'
	}

	/*
	 * An *empty* list is not evidence of anything - it is what a scheme that
	 * has not finished registering its features looks like, and it is what any
	 * future backend that emits one event per `enableFeature` would send first.
	 * The two mistakes are not symmetric: guessing `undefined` costs a spinner
	 * that the next event clears, while guessing `IN_PERSON` for an online
	 * scheme unmounts `ConfiguredVideoConferenceMedia` and kills the call. So
	 * in-person needs something positive on the list too.
	 *
	 * Verified on the wire: a stationary scheme sends `["ScriptFeatureType"]`,
	 * a remote one `["MicrophoneType","ScriptFeatureType","CameraType"]`.
	 */
	return schemeFeatures.length > 0 ? 'IN_PERSON' : undefined
}
